import { FC, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import FeatureItem from "@/components/UI/modal/PremiumSubscriptionModal/components/FeatureItem";
import PremiumSubscriptionModal from "@/components/UI/modal/PremiumSubscriptionModal/PremiumSubscriptionModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import {
  useCancelSubscriptionMutation,
  useGetMySubscriptionQuery,
  useGetPremiumSubscriptionPriceQuery,
} from "@/store/api/premiumApi";
import theme from "@/theme";
import { formatToLongDate, formatToShortDate } from "@/helpers/getHoursDifference";
import CancelSubscriptionModal from "@/components/UI/modal/CancelSubscriptionModal/CancelSubscriptionModal";

const Subscription: FC = () => {
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(false);
  const openPremiumModal = () => setPremiumModalOpen(true);
  const closePremiumModal = () => setPremiumModalOpen(false);
  const {
    data: priceData,
    isLoading: priceLoading,
    isError: priceError,
  } = useGetPremiumSubscriptionPriceQuery();
  const { data: subscriptionData } = useGetMySubscriptionQuery();
  const [cancelSubscription] = useCancelSubscriptionMutation();
  const activeSubscription = subscriptionData?.find((sub) => new Date(sub.endDate) > new Date());
  const isPremium = !!activeSubscription;
  // const isPremium = false;

  const isCancelled = activeSubscription?.status === "CANCELLED";
  const FEATURES = [
    "Blue tick on your user name",
    "Comment on any user's post",
    "See 10 results in trending instead of 3",
    "More recent time periods for sorting your main feed",
    "More recent time periods for sorting trending functions",
    "Posts sorting function exclusively from users you follow",
    "Sort trending functions for post data from the users you follow",
    "Get notifications from chosen users for their posts and comments",
    "Change the default amount you give for likes on the platform.",
  ];

  const handleCancelSubscription = async () => {
    try {
      if (activeSubscription?.uid) {
        await cancelSubscription(activeSubscription.uid).unwrap();
        setToast({ message: "Subscription successfully cancelled.", success: true });
      }
    } catch (error: any) {
      setToast({
        message: error?.data?.message || "An error occurred while cancelling the subscription.",
        success: false,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <PremiumSubscriptionModal
        open={premiumModalOpen}
        onClose={closePremiumModal}
        setToast={setToast}
      />
      <CancelSubscriptionModal
        open={cancelSubscriptionModalOpen}
        handleClose={() => setCancelSubscriptionModalOpen(false)}
        onDelete={handleCancelSubscription}
        time={formatToLongDate(activeSubscription?.endDate || "")}
      />
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <SettingTitle title="Premium Subscription" back isActive={true} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box
          sx={{
            width: "544px",
            height: "74px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            borderBottom: "1px solid #F5F5F5",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "528px",
              height: "50px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#2A2D31",
              }}
            >
              {isPremium ? "You’re a Premium member" : "Get Premium"}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "16px",
                color: "#7D8690",
              }}
            >
              {isPremium
                ? "You have a access to all of the Wethemrkt platform features"
                : "Unlock all features and enhance your expirience"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            maxWidth: 544,

            opacity: 1,
            gap: "10px",
            paddingTop: "10px",
            paddingLeft: "16px",
            paddingBottom: "28px",
            display: "flex",
            flexDirection: "column",
            borderBottom: isPremium ? "1px solid #F5F5F5" : "none",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0px",
              verticalAlign: "middle",
            }}
          >
            {isPremium
              ? "What you currently have with a premium subscription:"
              : "Premium Features"}
          </Typography>

          <Box>
            {FEATURES.map((label) => (
              <FeatureItem key={label} label={label} iconBgColor="#8BC5F480" iconColor="#2A85FF" />
            ))}
          </Box>
        </Box>

        {!isPremium ? (
          <Box
            sx={{
              width: 544,
              height: 74,
              opacity: 1,
              gap: "12px",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                width: 528,
                height: 50,
                borderRadius: "28px",
                textTransform: "none",
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "22px",
                letterSpacing: "0px",
                textAlign: "center",
                verticalAlign: "middle",
                color: "#FFFFFF",
                backgroundColor: isPremium ? "#F6F6F6" : "#2A85FF",
                "&:hover": {
                  backgroundColor: isPremium ? "#A0A0A0" : "#2573D6",
                },
                paddingTop: "18px",
                paddingBottom: "18px",
                boxShadow: "none",
              }}
              onClick={openPremiumModal}
              disabled={isPremium || priceLoading || priceError}
            >
              {isPremium
                ? "Subscribed"
                : priceLoading
                  ? "Loading..."
                  : priceError || !priceData
                    ? "Error"
                    : `Subscribe Now For ${priceData.value} USDC`}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "42px" }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", padding: "12px 16px", gap: "12px" }}
            >
              <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: theme.palette.custom.blueGray600,
                    fontWeight: 500,
                  }}
                >
                  Payment amount:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: theme.palette.custom.blueGray600,
                    fontWeight: 500,
                  }}
                >
                  {priceData?.value} USDC
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: theme.palette.custom.blueGray600,
                    fontWeight: 500,
                  }}
                >
                  {isCancelled ? "Expires:" : "Renews:"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: theme.palette.custom.blueGray600,
                    fontWeight: 500,
                  }}
                >
                  {formatToShortDate(activeSubscription?.endDate || "")}
                </Typography>
              </Box>
            </Box>
            {isCancelled ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    fontWeight: 500,
                    color: theme.palette.custom.blueGray600,
                    textAlign: "center",
                  }}
                >
                  Your subscription has been cancelled. You can renew after{" "}
                  {formatToShortDate(activeSubscription?.endDate || "")}.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{ display: "flex", justifyContent: "center", cursor: "pointer" }}
                onClick={() => setCancelSubscriptionModalOpen(true)}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    fontWeight: 500,
                    color: theme.palette.custom.secondaryRed,
                  }}
                >
                  Cancel subscription
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Subscription;
